import { useCallback } from "react"
import { batch } from "@preact/signals-react"
import { isEmpty, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS, REPORT_STATUS } from "@constants"
import { useToast } from "@hooks"
import { reportListData, reportListMultipleData, setReportData } from "../@signals"
import { useReportBackendApiPath } from "Pages/ReportsAlerts/Reports/@hooks/index"


export default function useReportMultipleList(selectedData, setSelectedRows = () => {
}) {
	const { backendUrl, reportType, forceUrl } = useReportBackendApiPath()
	const { showToast } = useToast()

	const msg = selectedData.length > 1
		? `these ${selectedData.length} reports`
		: 'the selected report'

	const handleClickMultipleOption = useCallback(key => batch(() => {
		reportListMultipleData.value = {
			values: [],
			key: key,
			idReport: selectedData.map(({ idReport }) => idReport),
			reportsInfo: selectedData
		}
	}), [JSON.stringify(selectedData)])

	const handleMultipleClone = useCallback(() => {
		showToast.confirm(`Are you sure to clone ${msg}?`, {
			title: 'Copy reports?',
			icon: 'copy',
			submitText: 'Copy',
			onSubmit: () => {
				showToast.promise(postSubmit(`${backendUrl}/clone-multi`, {
					idReport: selectedData.map(a => a.idReport),
				}).then(newData => {
					reportListData.value = [...newData, ...reportListData.value]
				}).catch(() => {
					showToast.error('Clone multi failed, please try again.')
				})).finally(() => {
					setSelectedRows([])
				})
			}
		})
	}, [JSON.stringify(selectedData)])

	const handlePerformMultipleExecutions = useCallback(() => {
		showToast.confirm(`Are you sure to perform ${msg}? Only non running reports will be executed`, {
			title: 'Perform reports?',
			tone: 'warning',
			icon: 'gears',
			submitText: 'Perform',
			onSubmit: () => {
				showToast.promise(postSubmit(`${BACKEND_APIS.SEARCH}/reports/execute`, {
					idReport: selectedData.map(a => a.idReport),
					isManualExecution: true,
					reportType: reportType
				}).then(res => {
					if (!isEmpty(res)) {
						batch(() => {
							Object.keys(res)?.map(idReport => setReportData({
								idReport: idReport,
								idLastExecution: res[idReport],
								statusLastExecution: REPORT_STATUS.RUNNING
							}))
						})
					} else {
						showToast.warning("Non valid reports were given, aborting task")
					}
				}).catch(() => {
					showToast.error('Report execution failed, please try again.')
					selectedData.map(a => setReportData({ idReport: a.idReport, statusLastExecution: REPORT_STATUS.READY }))
				})).finally(() => {
					setSelectedRows([])
				})
			}
		})
	}, [JSON.stringify(selectedData)])

	const handleStopMultipleExecutions = useCallback(() => {
		showToast.confirm(`Are you sure to stop ${msg}? Only running reports will be stopped `, {
			title: 'Stop executions?',
			tone: 'warning',
			icon: 'circle-stop',
			submitText: 'Stop',
			onSubmit: () => {
				selectedData.map(({ idReport }) => setReportData({ idReport: idReport, statusLastExecution: REPORT_STATUS.STOPPED }))
				showToast.promise(postSubmit(forceUrl, {
					reportExecutionID: selectedData.map(a => a.idLastExecution)
				}).finally(() => {
					setSelectedRows([])
				}))
			}
		})
	}, [JSON.stringify(selectedData)])

	const handleClickMultipleDelete = useCallback(() => {
		showToast.confirm(`Are you sure to delete ${selectedData.length} ${selectedData.length > 1 ? "reports" : "report"}?`, {
			title: 'Delete reports?',
			tone: 'danger',
			submitText: 'Delete',
			onSubmit: () => {
				reportListData.value = reportListData.value.filter(item => !selectedData.some(data => data.idReport === item.idReport))
				showToast.promise(postSubmit(`${backendUrl}/delete`, {
					idReport: selectedData.map(a => a.idReport)
				})).finally(() => {
					setSelectedRows([])
				})
			}
		})
	}, [selectedData])

	const handleClickMultipleDisable = useCallback(() => {
		showToast.confirm(`Are you sure to disable ${selectedData.length} ${selectedData.length > 1 ? "reports" : "report"}?`, {
			title: 'Disable reports?',
			icon: 'toggle-off',
			submitText: 'Disable',
			onSubmit: () => {
				selectedData.map(a => setReportData({ idReport: a.idReport, enabled: false }))
				showToast.promise(postSubmit(`${backendUrl}/disable`, {
					idReport: selectedData.map(a => a.idReport)
				})).finally(() => {
					setSelectedRows([])
				})
			}
		})
	}, [selectedData])

	const handleClickMultipleEnable = useCallback(() => {
		showToast.confirm(`Are you sure to enable ${selectedData.length} ${selectedData.length > 1 ? "reports" : "report"}?`, {
			title: 'Enable reports?',
			icon: 'toggle-on',
			submitText: 'Enable',
			onSubmit: () => {
				selectedData.map(a => setReportData({ idReport: a.idReport, enabled: true }))
				showToast.promise(postSubmit(`${backendUrl}/enable`, {
					idReport: selectedData.map(a => a.idReport)
				})).finally(() => {
					setSelectedRows([])
				})
			}
		})
	}, [selectedData])

	return {
		handleClickMultipleOption,
		handlePerformMultipleExecutions,
		handleClickMultipleDelete,
		handleStopMultipleExecutions,
		handleMultipleClone,
		handleClickMultipleDisable,
		handleClickMultipleEnable
	}
}